import { useEffect, useState } from 'react'
import { CreditCard, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { fetchPaymentMethodsList, updatePaymentMethod } from '@/lib/api/paymentMethods'
import { SALON_DESK_CHECKOUT } from '@/lib/salonDeskConfig'
import { cn } from '@/lib/utils'

import { SettingsSection } from './SettingsSection'

const inputClass = cn(
  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

export function PaymentLabelsSection({ embedded = false }) {
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  /** @type {[Array<{ id: number, code: string, name: string }>, Function]} */
  const [methods, setMethods] = useState([])

  useEffect(() => {
    let active = true
    setLoading(true)

    fetchPaymentMethodsList()
      .then((items) => {
        if (!active) {
          return
        }
        const allowed = new Set(SALON_DESK_CHECKOUT.allowedPaymentCodes)
        setMethods(
          items
            .filter((method) => allowed.has(method.code))
            .map((method) => ({
              id: method.id,
              code: method.code,
              name: method.name,
            })),
        )
      })
      .catch((err) => {
        if (active) {
          setError(err?.response?.data?.message ?? 'Unable to load payment methods.')
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false)
        }
      })

    return () => {
      active = false
    }
  }, [])

  /**
   * @param {number} id
   * @param {string} name
   */
  function updateName(id, name) {
    setMethods((current) =>
      current.map((method) => (method.id === id ? { ...method, name } : method)),
    )
    setSuccess('')
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')

    try {
      await Promise.all(
        methods.map((method) => updatePaymentMethod(method.id, { name: method.name })),
      )
      setSuccess('Payment labels saved.')
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to save payment labels.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <SettingsSection
      id="payments"
      icon={CreditCard}
      title="Payments"
      description="Labels shown at checkout for cash and card"
      embedded={embedded}
    >
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : methods.length === 0 ? (
        <p className="text-sm text-muted-foreground">No cash or card payment methods found.</p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {methods.map((method) => (
            <div key={method.id}>
              <label htmlFor={`payment-${method.code}`} className="mb-1.5 block text-sm font-medium">
                {method.code === 'cash' ? 'Cash label' : 'Card label'}
              </label>
              <input
                id={`payment-${method.code}`}
                type="text"
                value={method.name}
                onChange={(e) => updateName(method.id, e.target.value)}
                className={inputClass}
                required
              />
            </div>
          ))}
          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
          {success ? (
            <p className="text-sm text-primary" role="status">
              {success}
            </p>
          ) : null}
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save payment labels'}
          </Button>
        </form>
      )}
    </SettingsSection>
  )
}
